import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { nome: "", email: "", telefono: "", oggetto: "", messaggio: "" };

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!privacy) {
      toast.error("Per inviare il messaggio devi accettare l'informativa privacy.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contacts", { ...form, privacy });
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setForm(EMPTY);
      setPrivacy(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field =
    "h-12 rounded-xl border-[#C9DBDF] bg-[#F4F9FA] text-[#1A2E33] placeholder:text-[#9AB0B7] focus-visible:ring-[#1F8296]/50";

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-nome" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">
            Nome e cognome *
          </label>
          <Input
            id="contact-nome"
            data-testid="contact-name-input"
            required
            minLength={2}
            value={form.nome}
            onChange={set("nome")}
            placeholder="Mario Rossi"
            className={field}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">
            Email *
          </label>
          <Input
            id="contact-email"
            data-testid="contact-email-input"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="mario.rossi@email.it"
            className={field}
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-telefono" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">
            Telefono
          </label>
          <Input
            id="contact-telefono"
            data-testid="contact-phone-input"
            type="tel"
            value={form.telefono}
            onChange={set("telefono")}
            placeholder="333 1234567"
            className={field}
          />
        </div>
        <div>
          <label htmlFor="contact-oggetto" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">
            Oggetto *
          </label>
          <Input
            id="contact-oggetto"
            data-testid="contact-subject-input"
            required
            minLength={2}
            value={form.oggetto}
            onChange={set("oggetto")}
            placeholder="Informazioni sul corso base"
            className={field}
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-messaggio" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">
          Messaggio *
        </label>
        <Textarea
          id="contact-messaggio"
          data-testid="contact-message-input"
          required
          minLength={5}
          rows={6}
          value={form.messaggio}
          onChange={set("messaggio")}
          placeholder="Raccontaci cosa vorresti imparare e qual è il tuo livello di partenza..."
          className="rounded-xl border-[#C9DBDF] bg-[#F4F9FA] text-[#1A2E33] placeholder:text-[#9AB0B7] focus-visible:ring-[#1F8296]/50"
        />
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="contact-privacy"
          data-testid="contact-privacy-checkbox"
          checked={privacy}
          onCheckedChange={(v) => setPrivacy(v === true)}
          className="mt-0.5 border-[#9AB0B7] data-[state=checked]:border-[#2997AB] data-[state=checked]:bg-[#2997AB] data-[state=checked]:text-white"
        />
        <label htmlFor="contact-privacy" className="cursor-pointer text-xs leading-relaxed text-[#5C7178]">
          Ho letto l'<Link to="/privacy-policy" className="text-[#1F8296] underline underline-offset-2 hover:text-[#238496]">informativa privacy</Link> e
          acconsento al trattamento dei miei dati personali per essere ricontattato in merito alla mia richiesta. *
        </label>
      </div>

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={loading}
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2997AB] px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#238496] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />}
        {loading ? "Invio in corso..." : "Invia la richiesta"}
      </button>
    </form>
  );
}
